<template>
  <div>
    <div>
      <div
        class="rounded-2xl border px-5 py-4 bg-white/70 dark:bg-slate-800/70 dark:border-slate-600 border-slate-200 mb-4 hs-float-in">
        <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">Admin Dashboard</h1>
        <p class="text-sm text-slate-500 dark:text-slate-300 mt-1">Live system metrics, transaction activity, and
          operating health in one place.</p>
      </div>

      <div :class="[
        'dashboard-statscards',
        {
          'dashboard-statscards--no-data':
            filteredStatsCardItems.length === 0,
        },
      ]">
        <!-- Loading State -->
        <template v-if="loadingStats">
          <div v-for="item in statsCardItems" :key="item.iconType" class="flex">
            <StatsSkeleton />
          </div>
        </template>

        <!-- No Data Message -->
        <div v-else-if="filteredStatsCardItems.length === 0" class="col-span-full">
          <empty-state icon="bar-chart-2" title="No Data Available"
            description="There is no data available for the selected period." :show-suggestion="true"
            suggestion="Try selecting a different date range or check back later." />
        </div>

        <!-- Stats Cards -->
        <div v-else v-for="item in filteredStatsCardItems" :key="item.iconType" class="flex">
          <stats-card :iconType="item.iconType" :iconColor="item.iconColor" :statsNumber="item.statsNumber"
            :statsDescription="item.statsDescription" :cardIconClass="item.cardIconClass" />
        </div>
      </div>
      <div class="dashboard-bottom">
        <div class="dashboard-bottom__graph">
          <div class="dashboard-bottom__graph--diagram">
            <Linechart :labels="graphLabels" :data="graphData" :start-date="startDate" :end-date="endDate"
              :dynamic-title="dynamicTitle" :is-dark="isDark" @update:grouping="handleGroupingChange"
              @update:date-range="handleFilterDate" />
          </div>
        </div>
        <div class="dashboard-bottom__pie">
          <system-errors />
        </div>
      </div>
    </div>
    <div>
      <updatestore v-if="showModal" @close-modal="handleModalClose" />
    </div>
  </div>
</template>

<script>
import { useToggle, useDark } from "@vueuse/core";
import StatsSkeleton from "@/skeletons/StatsSkeleton";
import { format, isValid } from "date-fns";
import { toast } from "vue3-toastify";
import EmptyState from "@/components/common/EmptyState.vue";

export default {
  name: "DashboardView",
  components: {
    StatsSkeleton,
    EmptyState,
  },
  setup() {
    const isDark = useDark();
    const toggleTheme = useToggle(isDark);
    const toggle = () => {
      useToggle(isDark);
      toggleTheme();
    };

    return {
      isDark,
      toggleTheme,
      toggle,
    };
  },

  data() {
    return {
      statsCardItems: [
        {
          key: "users",
          iconType: "users",
          iconColor: "text-emerald-600 dark:text-emerald-400",
          statsNumber: "30",
          statsDescription: "Total Users",
          cardIconClass: "bg-emerald-100 dark:bg-emerald-900/20",
        },
        {
          key: "stores",
          iconType: "shopping-bag",
          iconColor: "text-blue-600 dark:text-blue-400",
          statsNumber: "60",
          statsDescription: "Total Stores",
          cardIconClass: "bg-blue-100 dark:bg-blue-900/20",
        },
        {
          key: "transactions",
          iconType: "gift",
          iconColor: "text-red-600 dark:text-red-400",
          statsNumber: "600",
          statsDescription: "Total Transactions",
          cardIconClass: "bg-red-100 dark:bg-red-900/20",
        },
        {
          key: "products",
          iconType: "life-buoy",
          iconColor: "text-indigo-600 dark:text-indigo-400",
          statsNumber: "100",
          statsDescription: "Total Products",
          cardIconClass: "bg-indigo-100 dark:bg-indigo-900/20",
        },
      ],
      showSidebar: false,
      showModal: false,
      graphLabels: [],
      graphData: [],
      startDate: new Date(new Date() - new Date(30 * 24 * 60 * 60 * 1000)),
      endDate: new Date(),
      dynamicTitle: "Last 30 days",
      stores: [],
    };
  },
  computed: {
    loadingStats() {
      return this.$store.getters.isLoadingStats;
    },
    filteredStatsCardItems() {
      return this.statsCardItems.filter((item) => {
        // Filter out cards with zero values
        const value = item.statsNumber;
        const numericValue = parseFloat(value);
        return !isNaN(numericValue) && numericValue > 0;
      });
    },
  },
  methods: {
    handleSidebar() {
      this.showSidebar = !this.showSidebar;
    },
    handleModalClose() {
      this.showModal = false;
    },
    toggleProfileModal() {
      this.showProfileModal = !this.showProfileModal;
    },

    handleUpdateStartDate(newStartDate) {
      this.startDate = newStartDate;
    },
    handleUpdateEndDate(newEndDate) {
      this.endDate = newEndDate;
    },

    handleFilterDate(newDateRange) {
      if (!newDateRange) return;

      let startDate = "";
      let endDate = "";

      if (isValid(newDateRange.start) && isValid(newDateRange.end)) {
        startDate = format(newDateRange.start, "dd/MM/yyyy HH:mm:ss");
        endDate = format(newDateRange.end, "dd/MM/yyyy HH:mm:ss");
        this.dynamicTitle = `${format(
          newDateRange.start,
          "MMM d, yyyy"
        )} - ${format(newDateRange.end, "MMM d, yyyy")}`;
        this.$emit("update:dynamic-title", this.dynamicTitle);
      } else {
        toast.error("Invalid date values");
      }
      this.$store
        .dispatch("getStoreTransactionsCountDashboardData", {
          start_time: startDate,
          end_time: endDate,
          grouping: this.grouping,
        })
        .then((res) => {
          this.graphData = res.values;
          this.graphLabels = res.labels;
        });
    },
    handleGroupingChange(newGrouping) {
      this.grouping = newGrouping;
      this.handleFilterDate({
        start: this.startDate,
        end: this.endDate,
      });
    },
    handleMyStores() {
      this.$router.push("/store");
    },
  },
  async mounted() {
    try {
      const storesData = await this.$store.dispatch("getUserStores");
      this.stores = storesData;
      if (storesData?.length > 0) {
        this.$store.commit("SET_SELECTED_STORE", storesData[0]);
      }
    } catch (error) {
      toast.error("Error fetching stores");
    }

    this.$store.dispatch("getDashboardStats").then((res) => {
      this.statsCardItems.find((item) => item.key === "users").statsNumber =
        res.total_users;
      this.statsCardItems.find((item) => item.key === "stores").statsNumber =
        res.total_stores;
      this.statsCardItems.find(
        (item) => item.key === "transactions"
      ).statsNumber = res.total_transactions;
      this.statsCardItems.find((item) => item.key === "products").statsNumber =
        res.total_products;
    });
  },
};
</script>
